import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { Icon, iconsList } from "../../Icon";
import { Card } from "../Card";
import styles from "./HeroCard.module.scss";

const HeroCard = ({
	className,
	style,
	image,
	header,
	headingSize,
	headingWeight,
	color,
	uppercase,
	subheader,
	subheader2,
	content,
	button,
	link,
	icon,
	backgroundColor,
	slot,
	variant,
	renderImage,
	renderLink,
}) => (
	<div className={cx(className)}>
		<Card
			className={cx(styles.hero, slot && styles.slot, variant && styles[variant])}
			style={{ ...style, backgroundColor }}>
			<div className={styles.heroImage}>
				<Card.Image src={image?.src} alt={image?.alt} imageComponentProps={{ renderImage }} />
			</div>
			{header || link?.text ? (
				<Card.Body className={styles.heroBody} style={{ color }}>
					{header && (
						<div>
							{header && (
								<Card.Header
									size={headingSize}
									weight={headingWeight}
									uppercase={uppercase}
									className={styles.heroHeading}
									style={{ color }}>
									{header}
								</Card.Header>
							)}
							{subheader && (
								<Card.Subheader
									size={headingSize}
									weight={headingWeight}
									className={styles.heroSubheading}
									style={{ color }}>
									{subheader}
									{subheader2 && <span>{subheader2}</span>}
								</Card.Subheader>
							)}
						</div>
					)}
					{button?.to && (
						<Card.Button
							to={button?.to}
							target={button?.target}
							label={button?.label}
							className={styles.heroButton}
							icon={button?.icon}
							iconPlacement={button?.iconPlacement}
							linkComponentProps={{ renderLink }}>
							{button?.text}
						</Card.Button>
					)}
					{link?.to && !button && (
						<Card.Link
							to={link?.to}
							target={link?.target}
							className={cx(styles.heroLink, !header && styles.heroLinkFull)}
							renderLink={renderLink}>
							{!header ? (
								<Card.Header
									size={headingSize}
									weight={headingWeight}
									uppercase={uppercase}
									className={styles.heroLinkContainer}
									style={{ color }}>
									{icon && <Icon name={icon} style={{ color }} />}
									{link?.text}
									<Icon name="arrow-right" style={{ color }} />
								</Card.Header>
							) : (
								<div
									className={cx(styles.heroLinkContainer, styles.heroLinkContainerFull)}
									style={{ color }}>
									{icon && <Icon name={icon} style={{ color }} />}
									{link?.text}
									<Icon name="arrow-right" style={{ color }} />
								</div>
							)}
						</Card.Link>
					)}
				</Card.Body>
			) : (
				<></>
			)}
			{content && (
				<Card.Content className={styles.heroContent}>
					<span dangerouslySetInnerHTML={{ __html: content }} />
				</Card.Content>
			)}
			{link?.to && !button && (
				<Card.AsLink
					link={link?.to}
					target={link?.target}
					text={header || link?.text}
					className={styles.primaryLink}
					renderLink={renderLink}
				/>
			)}
			{button?.to && (
				<Card.AsLink
					link={button?.to}
					target={button?.target}
					text={button?.label || button?.text}
					className={styles.primaryLink}
					renderLink={renderLink}
				/>
			)}
		</Card>
	</div>
);

HeroCard.propTypes = {
	className: PropTypes.string,
	style: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
	image: PropTypes.shape({
		source: PropTypes.shape({
			src: PropTypes.string,
			srcSet: PropTypes.string,
			sizes: PropTypes.string,
		}),
		alt: PropTypes.string,
		src: PropTypes.string,
	}).isRequired,
	header: PropTypes.string,
	color: PropTypes.string,
	headingSize: PropTypes.string,
	headingWeight: PropTypes.string,
	uppercase: PropTypes.bool,
	subheader: PropTypes.string,
	/**
	 * If there are 2 subheaders
	 */
	subheader2: PropTypes.string,
	content: PropTypes.string,
	/**
	 * Buttons act as links but look like buttons in the UI . They will be applied to card and cannot be used with links
	 */
	button: PropTypes.shape({
		icon: PropTypes.oneOf(iconsList),
		iconPlacement: PropTypes.oneOf(["right", "left"]),
		onClick: PropTypes.func,
		label: PropTypes.string,
		to: PropTypes.string,
		target: PropTypes.string,
		text: PropTypes.string,
	}),
	/**
	 * Links will be applied to card, cannot be used with buttons
	 */
	link: PropTypes.shape({
		to: PropTypes.string.isRequired,
		target: PropTypes.string,
		icon: PropTypes.oneOf(iconsList),
		text: PropTypes.string,
	}),
	/**
	 * Renders icons with buttons or links
	 */
	icon: PropTypes.oneOf(iconsList),
	/**
	 * Changes background colour of the the hero card
	 */
	backgroundColor: PropTypes.string,
	/**
	 * Sets Flag for smaller/other cards beside featured card for controlling layouts
	 */
	slot: PropTypes.bool,
	/**
	 * Sets flag for cards that are links, with no buttons or visible links
	 */
	variant: PropTypes.oneOf([PropTypes.undefined, "highlight"]),
	renderImage: PropTypes.func,
	renderLink: PropTypes.func,
};

HeroCard.defaultProps = {
	className: undefined,
	style: {},
	header: undefined,
	headingSize: "h3",
	headingWeight: "bold",
	color: "#FFFFFF",
	uppercase: true,
	subheader: undefined,
	subheader2: undefined,
	content: undefined,
	button: undefined,
	link: undefined,
	icon: undefined,
	backgroundColor: "#ff6000",
	slot: false,
	variant: undefined,
	renderImage: undefined,
	renderLink: undefined,
};

export default HeroCard;
export { HeroCard };
